import { Defs, Document, Image, Link, LinearGradient, Page, RadialGradient, Rect, StyleSheet, Stop, Svg, Text, View } from '@react-pdf/renderer';
import bluwaveLogo from '../assets/BluWave-logo_final.png';
import { renderRichHtml, richTextStyles } from '../utils/pdfRichText';
import {
  BookOpenIcon,
  BriefcaseIcon,
  CalendarIcon,
  ClockIcon,
  DollarSignIcon,
  FileTextIcon,
  Link2Icon,
  MapPinIcon,
} from './pdfIcons';

export type InterimOnePagerPdfProps = {
  fullName: string;
  tagline?: string;
  location?: string;
  linkedinUrl?: string;
  resumeUrl?: string;

  magicLinkUrl?: string;
  inviteTeamMemberUrl?: string;

  generalBioHtml?: string;
  snapshotTitle: string;
  snapshotHtml?: string;
  whyWeTitle: string;
  whyWeHtml?: string;

  startDate?: string;
  onsiteCommitment?: string;
  engagementRate?: string;
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 32,
    paddingBottom: 70,
    paddingHorizontal: 36,
    fontSize: 11,
    color: '#111827',
    fontFamily: 'Helvetica',
  },
  section: {
    marginBottom: 18,
  },
  sectionLast: {
    marginBottom: 0,
  },
  headerBanner: {
    position: 'relative',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(19,37,67,0.12)',
    backgroundColor: '#0e1d36',
    overflow: 'hidden',
    paddingHorizontal: 24,
    paddingVertical: 20,
    marginBottom: 18,
  },
  headerGradientLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  headerTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerTitleBlock: {
    flex: 1,
    paddingRight: 12,
  },
  headerName: {
    fontSize: 22,
    fontWeight: 700,
    color: '#FFFFFF',
  },
  headerTagline: {
    marginTop: 4,
    fontSize: 11,
    color: '#FFFFFF',
  },
  headerButtonsRow: {
    flexDirection: 'row',
    flexShrink: 0,
    gap: 6,
  },
  inviteButton: {
    borderWidth: 1,
    borderColor: '#FFFFFF',
    borderRadius: 6,
    paddingVertical: 7,
    paddingHorizontal: 10,
  },
  inviteButtonText: {
    color: '#FFFFFF',
    fontSize: 9,
    fontWeight: 700,
  },
  introButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    paddingVertical: 7,
    paddingHorizontal: 10,
  },
  introButtonText: {
    color: '#132543',
    fontSize: 9,
    fontWeight: 700,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
    backgroundColor: 'rgba(255,255,255,0.14)',
    borderRadius: 12,
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  chipText: {
    fontSize: 10,
    fontWeight: 500,
    color: '#FFFFFF',
  },
  overviewCard: {
    backgroundColor: 'rgba(83, 190, 232, 0.1)',
    borderRadius: 12,
    paddingTop: 14,
    paddingBottom: 12,
    paddingHorizontal: 16,
  },
  overviewTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    marginBottom: 8,
  },
  overviewTitle: {
    ...richTextStyles.h2,
    marginBottom: 0,
  },
  availabilityBox: {
    borderWidth: 1,
    borderColor: 'rgba(110,191,164,.45)',
    borderRadius: 12,
    overflow: 'hidden',
  },
  availabilityHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    backgroundColor: 'rgba(110,191,164,.28)',
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  availabilityHeaderText: {
    fontSize: 12,
    fontWeight: 700,
    color: '#1f6049',
  },
  availabilityCellsRow: {
    flexDirection: 'row',
  },
  availabilityCell: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRightWidth: 1,
    borderRightColor: 'rgba(110,191,164,.28)',
  },
  availabilityCellLast: {
    borderRightWidth: 0,
  },
  availabilityLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginBottom: 6,
  },
  availabilityLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: '#2f7d5e',
  },
  availabilityValue: {
    fontSize: 12,
    fontWeight: 700,
    color: '#132543',
  },
  feesBox: {
    borderWidth: 1,
    borderColor: '#E2E2E2',
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 16,
  },
  feesLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginBottom: 8,
  },
  feesLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: '#60667C',
  },
  feesValue: {
    fontSize: 20,
    fontWeight: 700,
    color: '#132543',
  },
  divider: {
    marginTop: 12,
    height: 2,
    width: '100%',
    backgroundColor: '#E8E8E8',
  },
  footerWrap: {
    position: 'absolute',
    left: 36,
    right: 36,
    bottom: 20,
  },
  footerDivider: {
    width: '100%',
    height: 2,
    backgroundColor: '#D1D5DB',
  },
  footerLogoRow: {
    width: '100%',
    paddingTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerDisclaimer: {
    fontSize: 7,
    color: '#9CA3AF',
    flex: 1,
    paddingRight: 12,
  },
  footerLogo: {
    height: 18,
    width: 90,
    objectFit: 'contain',
  },
});

export default function InterimOnePagerPdf(props: InterimOnePagerPdfProps) {
  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.footerWrap} fixed>
          <View style={styles.footerDivider} />
          <View style={styles.footerLogoRow}>
            <Text style={styles.footerDisclaimer}>
              Derived from BluWave's knowledge of the provider. Not liable for inaccuracies — independent verification recommended.
            </Text>
            <Image src={bluwaveLogo} style={styles.footerLogo} />
          </View>
        </View>

        <View style={styles.headerBanner} wrap={false}>
          <View style={styles.headerGradientLayer}>
            <Svg viewBox="0 0 1 1" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
              <Defs>
                <LinearGradient id="headerBase" x1="0" y1="0" x2="1" y2="0.6">
                  <Stop offset="0" stopColor="#0e1d36" />
                  <Stop offset="0.46" stopColor="#163056" />
                  <Stop offset="1" stopColor="#1f4b86" />
                </LinearGradient>
                <RadialGradient id="headerGlowGreen" cx="0.12" cy="1" r="0.95" gradientUnits="objectBoundingBox">
                  <Stop offset="0" stopColor="#6ebfa4" stopOpacity={0.35} />
                  <Stop offset="0.6" stopColor="#6ebfa4" stopOpacity={0} />
                </RadialGradient>
                <RadialGradient id="headerGlowBlue" cx="0.88" cy="0" r="0.95" gradientUnits="objectBoundingBox">
                  <Stop offset="0" stopColor="#53bee8" stopOpacity={0.55} />
                  <Stop offset="0.55" stopColor="#53bee8" stopOpacity={0} />
                </RadialGradient>
              </Defs>
              <Rect x={0} y={0} width={1} height={1} fill="url(#headerBase)" />
              <Rect x={0} y={0} width={1} height={1} fill="url(#headerGlowGreen)" />
              <Rect x={0} y={0} width={1} height={1} fill="url(#headerGlowBlue)" />
            </Svg>
          </View>

          <View style={styles.headerTopRow}>
            <View style={styles.headerTitleBlock}>
              <Text style={styles.headerName}>{props.fullName || 'Provider'}</Text>
              {!!props.tagline && <Text style={styles.headerTagline}>{props.tagline}</Text>}
            </View>
            {(!!props.magicLinkUrl || !!props.inviteTeamMemberUrl) && (
              <View style={styles.headerButtonsRow} wrap={false}>
                {!!props.inviteTeamMemberUrl && (
                  <Link src={props.inviteTeamMemberUrl} style={{ textDecoration: 'none' }}>
                    <View style={styles.inviteButton}>
                      <Text style={styles.inviteButtonText}>Invite Team Member</Text>
                    </View>
                  </Link>
                )}
                {!!props.magicLinkUrl && (
                  <Link src={props.magicLinkUrl} style={{ textDecoration: 'none' }}>
                    <View style={styles.introButton}>
                      <Text style={styles.introButtonText}>Get Introduced</Text>
                    </View>
                  </Link>
                )}
              </View>
            )}
          </View>
          <View style={styles.chipRow}>
            {!!props.location && (
              <View style={styles.chip}>
                <MapPinIcon size={10} color="#FFFFFF" />
                <Text style={styles.chipText}>{props.location}</Text>
              </View>
            )}
            {!!props.linkedinUrl && (
              <Link src={props.linkedinUrl} style={{ textDecoration: 'none' }}>
                <View style={styles.chip}>
                  <Link2Icon size={10} color="#FFFFFF" />
                  <Text style={styles.chipText}>LinkedIn</Text>
                </View>
              </Link>
            )}
            {!!props.resumeUrl && (
              <Link src={props.resumeUrl} style={{ textDecoration: 'none' }}>
                <View style={styles.chip}>
                  <FileTextIcon size={10} color="#FFFFFF" />
                  <Text style={styles.chipText}>Resume</Text>
                </View>
              </Link>
            )}
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.overviewCard}>
            <View style={styles.overviewTitleRow}>
              <BookOpenIcon size={13} color="#4BABE0" />
              <Text style={styles.overviewTitle}>Executive Overview</Text>
            </View>
            {props.generalBioHtml ? renderRichHtml(props.generalBioHtml, 'overview') : null}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={richTextStyles.h2}>{props.snapshotTitle}</Text>
          {props.snapshotHtml ? renderRichHtml(props.snapshotHtml, 'snapshot') : null}
        </View>

        <View style={styles.section}>
          <Text style={richTextStyles.h2}>{props.whyWeTitle}</Text>
          {props.whyWeHtml ? renderRichHtml(props.whyWeHtml, 'whywe') : null}
        </View>

        <View style={styles.section}>
          <View style={styles.availabilityBox} wrap={false}>
            <View style={styles.availabilityHeaderRow}>
              <CalendarIcon size={11} color="#2f7d5e" />
              <Text style={styles.availabilityHeaderText}>AVAILABILITY</Text>
            </View>
            <View style={styles.availabilityCellsRow}>
              <View style={styles.availabilityCell}>
                <View style={styles.availabilityLabelRow}>
                  <ClockIcon size={9} color="#2f7d5e" />
                  <Text style={styles.availabilityLabel}>START DATE</Text>
                </View>
                <Text style={styles.availabilityValue}>{props.startDate || 'Not yet available'}</Text>
              </View>
              <View style={styles.availabilityCell}>
                <View style={styles.availabilityLabelRow}>
                  <BriefcaseIcon size={9} color="#2f7d5e" />
                  <Text style={styles.availabilityLabel}>ON-SITE COMMITMENT</Text>
                </View>
                <Text style={styles.availabilityValue}>{props.onsiteCommitment || 'Not yet available'}</Text>
              </View>
              <View style={[styles.availabilityCell, styles.availabilityCellLast]}>
                <View style={styles.availabilityLabelRow}>
                  <MapPinIcon size={9} color="#2f7d5e" />
                  <Text style={styles.availabilityLabel}>LOCATION</Text>
                </View>
                <Text style={styles.availabilityValue}>{props.location || 'Not specified'}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={[styles.section, styles.sectionLast]} wrap={false}>
          <View style={styles.feesBox}>
            <View style={styles.feesLabelRow}>
              <DollarSignIcon size={9} color="#132543" />
              <Text style={styles.feesLabel}>ENGAGEMENT RATE</Text>
            </View>
            <Text style={styles.feesValue}>{props.engagementRate || 'Rate not yet available'}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}
